// The one floating tooltip this chart keeps: the cards a Note pin, an Alarm mark or a Feed Mix
// block opens, plus the pointer tracking that places them and the rule that closes them again.
//
// Everything else on this chart is reported by the fixed readout row, and a floating tooltip tall
// enough to sit on stacked bands would cover the very data it describes - so the formatter below
// returns false for all of them, and these three are what it keeps as the exception: a mark is a
// single glyph carrying a date and a message that appear nowhere else. The context bars need no
// such help - each already draws its own name inside itself.

import type { Chart } from 'highcharts';
import { i18n } from '@/plugins/internationalization/i18n';
import { withAlpha } from '@/plugins/highcharts/chartColors';
import colors from '@/plugins/tailwind';
import type { BandLayout } from './productivityBands';
import { CHART_CHROME_COLORS } from './productivityColors';
import { ALARM_SERIES_ID, FEED_MIX_BORDER_COLOR, FEED_MIX_SERIES_ID } from './productivityContextBands';
import { escapeHtml, productivityDate } from './productivityFormatting';
import { bandBoundsPx } from './productivityOverlay';

// A little slack at the top: an Alarm triangle is centred just inside the band, so its upper half is
// drawn above the axis line it belongs to.
const EVENT_LANE_TOP_SLACK_PX = 10;

// Enough to clear a Note pin, which hangs about 21px below its own anchor.
const CARD_GAP_PX = 24;

// A small light card rather than Highcharts' dark default block of text. These tooltips carry
// free text a person wrote - a Note's own wording, an Alarm's recorded Cause - often several lines
// of it, and a run of pale text on near-black with no structure is where that became unreadable.
// The kind is named and colour-keyed to the mark that was hovered, the date is subordinate to it,
// and the message itself gets the body treatment it needs to be read as prose.
const TOOLTIP_CARD_SHADOW = `0 6px 16px ${withAlpha(colors.slate[900], 0.12)}`;
const TOOLTIP_CARD_OPEN = `<div style="min-width:190px;max-width:280px;padding:8px 10px;border-radius:8px;background:${colors.white};border:1px solid ${colors.gray[200]};box-shadow:${TOOLTIP_CARD_SHADOW}">`;

function eventTooltipHtml(when: string, name: string, color: string, text: string): string {
    // `text` arrives as markup, not as prose - a cluster of Notes under one pin is a ruled-off count
    // and a list of entries (clusterText), whose own free text is escaped where it is embedded.
    const body = text ? `<div style="color:${colors.gray[700]};line-height:1.5;white-space:normal">${text}</div>` : '';
    return [
        TOOLTIP_CARD_OPEN,
        '<div style="display:flex;align-items:center;gap:6px;margin-bottom:5px">',
        `<span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${color};flex:0 0 8px"></span>`,
        `<span style="font-weight:700;color:${colors.gray[900]}">${escapeHtml(name)}</span>`,
        `<span style="margin-left:auto;color:${colors.gray[400]};font-size:10px;white-space:nowrap">${when}</span>`,
        '</div>',
        body,
        '</div>',
    ].join('');
}

// The Feed Mix card differs from the event one in where the date goes: a mix name runs to fifty
// characters and has to be allowed to wrap, which leaves no room for a date on the same line - and
// a block spans a period rather than marking an instant, so it is two dates rather than one.
function feedMixTooltipHtml(name: string, when: string, remarks: string[]): string {
    return [
        TOOLTIP_CARD_OPEN,
        '<div style="display:flex;align-items:flex-start;gap:6px">',
        `<span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${FEED_MIX_BORDER_COLOR};flex:0 0 8px;margin-top:4px"></span>`,
        `<span style="font-weight:700;color:${colors.gray[900]};white-space:normal">${escapeHtml(name)}</span>`,
        '</div>',
        `<div style="color:${colors.gray[400]};font-size:10px;margin-top:3px">${when}</div>`,
        ...remarks.map((remark) => `<div style="color:${colors.gray[700]};line-height:1.45;white-space:normal;margin-top:5px">${escapeHtml(remark)}</div>`),
        '</div>',
    ].join('');
}

// The two series whose points carry a message of their own rather than a value: Notes and Alarms.
function isEventSeries(series: { type?: string; options?: { id?: string } } | undefined): boolean {
    return series?.type === 'flags' || series?.options?.id === ALARM_SERIES_ID;
}

export type ProductivityEventTooltip = {
    // Recorded from `mouseover` as well as `mousemove`, and in the capture phase for both. A mark's
    // tracker opens its tooltip on mouseover, which is the one moment no mousemove has been seen yet -
    // so tracking movement alone left the first card of a visit placed from the point's own anchor.
    recordPointerX(chart: Chart, event: MouseEvent): void;
    /**
     * Closes a Note or Alarm tooltip once the cursor leaves the band those marks live in.
     *
     * The flags series has to keep Highcharts' sticky tracking - without it the pins stop answering to
     * hover entirely - and sticky tracking's whole behaviour is to hold the tooltip open until the
     * pointer finds another series or leaves the plot. On a chart whose event marks sit in a 64px strip
     * above several hundred pixels of data bands, that means a Note tooltip follows the reader down
     * across the weight curve and the water columns, covering them.
     *
     * The band a mark lives in is exactly the region where its own tooltip is meaningful, so leaving
     * that band is the signal to close - see openTooltipBandBoundsPx for why it is the one band rather
     * than both context bands together.
     */
    hideWhenAway(chart: Chart, event: MouseEvent): void;
    positioner: (this: { chart: Chart }, labelWidth: number, labelHeight: number, point: { plotX: number; plotY: number; series?: { yAxis?: { top?: number } } }) => { x: number; y: number };
    formatter: (this: Record<string, any>) => string | false;
};

export function createProductivityEventTooltip(bandLayout: () => BandLayout): ProductivityEventTooltip {
    // Where the cursor last was, in chart pixels. The tooltip's positioner needs it: Highcharts hands a
    // positioner the *point's* own anchor, which for a Feed Mix block is its start and says nothing
    // about where along a months-wide bar the reader actually is.
    let lastPointerChartX: number | null = null;

    // Which band the card currently on screen belongs to, recorded by the formatter as it builds one.
    // Per band, not the two context bands together: the Note and Alarm marks live in the Production Group
    // band and the Feed Mix blocks in the band below it, and with one shared lane a card stayed open
    // while the reader moved between them - which is the move most likely to be made, the two being
    // adjacent and both worth reading.
    //
    // Recorded rather than read back off `chart.hoverPoint`, which is cleared the moment the pointer
    // leaves the mark and so is already gone by the time this has to decide.
    let openTooltipBandId: 'productionGroups' | 'feedMixes' | null = null;

    // Both context bands together, which is where any of these tooltips can be seated (see the
    // positioner).
    function contextLaneBoundsPx(chart: Chart): { top: number; bottom: number } | null {
        const layout = bandLayout();
        const bounds = [layout.productionGroups, layout.feedMixes]
            .map((box) => bandBoundsPx(chart, box))
            .filter((box): box is NonNullable<typeof box> => box !== null);
        if (bounds.length === 0) return null;
        return { top: Math.min(...bounds.map((box) => box.top)) - EVENT_LANE_TOP_SLACK_PX, bottom: Math.max(...bounds.map((box) => box.bottom)) };
    }

    function openTooltipBandBoundsPx(chart: Chart): { top: number; bottom: number } | null {
        if (openTooltipBandId === null) return contextLaneBoundsPx(chart);
        if (openTooltipBandId === 'feedMixes') return bandBoundsPx(chart, bandLayout().feedMixes);
        const productionGroupBand = bandBoundsPx(chart, bandLayout().productionGroups);
        return productionGroupBand === null ? null : { top: productionGroupBand.top - EVENT_LANE_TOP_SLACK_PX, bottom: productionGroupBand.bottom };
    }

    return {
        recordPointerX(chart: Chart, event: MouseEvent): void {
            const normalized = chart.pointer?.normalize(event);
            if (normalized) lastPointerChartX = normalized.chartX;
        },

        hideWhenAway(chart: Chart, event: MouseEvent): void {
            const normalized = chart.pointer?.normalize(event);
            if (!normalized) return;
            lastPointerChartX = normalized.chartX;

            const lane = openTooltipBandBoundsPx(chart);
            if (!lane) return;
            if (normalized.chartY >= lane.top && normalized.chartY <= lane.bottom) return;
            chart.tooltip?.hide(0);
            openTooltipBandId = null;
        },

        // Anchored to the mark the reader is pointing at, above it where it fits and just below it
        // where it does not. Highcharts' own placement cannot be left to it: every card here belongs
        // to a mark in the context bands at the very top of the plot, and a Feed Mix card - a wrapped
        // name, a span and its remarks - was drawn clipped by the plot's top edge. Seating every card
        // below both context bands cures the clipping and costs the Note and Alarm cards their
        // proximity to the pin they describe. Below the *mark* keeps both: the tall card still cannot
        // clip, and a Note's card sits against its own pin rather than two bands away from it.
        positioner(this: { chart: Chart }, labelWidth: number, labelHeight: number, point: { plotX: number; plotY: number; series?: { yAxis?: { top?: number } } }) {
            const chart = this.chart;
            const left = chart.plotLeft;
            const top = chart.plotTop;
            const floor = top + chart.plotHeight;
            // The mark's own y, in chart pixels. `point.plotY` is measured from the axis the point
            // belongs to, and every mark here sits on one of the thin context axes partway down the
            // plot - so without its axis' own offset a card is placed a whole band away from what it
            // describes.
            const anchorY = (point.series?.yAxis?.top ?? top) + point.plotY;
            const below = anchorY + CARD_GAP_PX;
            // Centred on the cursor rather than on the point. A Feed Mix block is anchored at its own
            // start and can be months wide, so centring on the point put its card at the far left of
            // the plot - nowhere near the block the reader was pointing at.
            const anchorX = lastPointerChartX ?? left + point.plotX;
            return {
                x: Math.min(Math.max(anchorX - labelWidth / 2, left), left + chart.plotWidth - labelWidth),
                // Below the mark by preference, above it only where there is no room below. The marks
                // are read left to right along their band, so a card hanging below covers the least:
                // above, it sits on the row the reader is scanning.
                y: below + labelHeight <= floor ? below : Math.max(top, anchorY - labelHeight - CARD_GAP_PX),
            };
        },

        formatter(this: Record<string, any>): string | false {
            // The tooltip is shared, so `this.points` holds Highcharts' *label config* objects rather
            // than the points themselves - and a config carries only the handful of fields the default
            // formatter needs. Everything these cards are built from (a block's name, the remarks on
            // it) lives on the Point, which the config points back at - read off the config directly,
            // the card comes out nameless.
            const context = this.point ?? this.points?.[0];
            const point = context?.point ?? context;
            openTooltipBandId = point?.series?.options?.id === FEED_MIX_SERIES_ID ? 'feedMixes' : 'productionGroups';
            if (point?.series?.options?.id === FEED_MIX_SERIES_ID) {
                const endMs = point.custom?.endMs ?? null;
                const from = productivityDate(point.x, 'middle');
                // A block still running has no closing date to state, so the opening one stands alone
                // rather than being fed to a range whose other half would be blank.
                const dates =
                    endMs === null
                        ? from
                        : (i18n.global.t('views.monitoring.activeGroups.texts.dateRange', { from, to: productivityDate(endMs, 'middle') }) as string);
                return feedMixTooltipHtml(String(point.name ?? ''), dates, (point.custom?.remarks ?? []) as string[]);
            }
            if (!isEventSeries(point?.series)) return false;
            return eventTooltipHtml(
                productivityDate(point.x, 'middle'),
                String(point.series.name ?? ''),
                String(point.color ?? point.series.color ?? CHART_CHROME_COLORS.eventSwatchFallback),
                String(point.text ?? '')
            );
        },
    };
}
